import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase, faHeart, faBook, faCoins, faHandshake, faRocket,
  faTrophy, faBolt, faStar, faFire, faArrowRight, faPlus,
  faChartLine, faMedal, faCalendarCheck, faCalendarDay, faCalendarWeek,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import api from '../api/API';
import './LevelUpHubPage.css';

const LEVELS = [
  { level: 1,  title: 'Explorador',   xpNeeded: 0 },
  { level: 2,  title: 'Aprendiz',     xpNeeded: 500 },
  { level: 3,  title: 'Praticante',   xpNeeded: 1500 },
  { level: 4,  title: 'Especialista', xpNeeded: 3000 },
  { level: 5,  title: 'Profissional', xpNeeded: 5500 },
  { level: 6,  title: 'Sênior',       xpNeeded: 9000 },
  { level: 7,  title: 'Referência',   xpNeeded: 14000 },
  { level: 8,  title: 'Expert',       xpNeeded: 21000 },
  { level: 9,  title: 'Mestre',       xpNeeded: 30000 },
  { level: 10, title: 'Lenda',        xpNeeded: 42000 },
];

export const AREA_CONFIG = [
  { id: 'carreira',        label: 'Carreira',          icon: faBriefcase, color: '#6366f1', glow: 'rgba(99,102,241,0.4)' },
  { id: 'saude',           label: 'Saúde',             icon: faHeart,     color: '#ec4899', glow: 'rgba(236,72,153,0.4)' },
  { id: 'aprendizado',     label: 'Aprendizado',       icon: faBook,      color: '#8b5cf6', glow: 'rgba(139,92,246,0.4)' },
  { id: 'financas',        label: 'Finanças',          icon: faCoins,     color: '#f59e0b', glow: 'rgba(245,158,11,0.4)' },
  { id: 'relacionamentos', label: 'Relacionamentos',   icon: faHandshake, color: '#10b981', glow: 'rgba(16,185,129,0.4)' },
  { id: 'projetos',        label: 'Projetos Pessoais', icon: faRocket,    color: '#3b82f6', glow: 'rgba(59,130,246,0.4)' },
];

export const getLevelInfo = (xp) => {
  let current = LEVELS[0];
  let next = LEVELS[1];
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].xpNeeded) {
      current = LEVELS[i];
      next = LEVELS[i + 1] || null;
      break;
    }
  }
  const xpInLevel = xp - current.xpNeeded;
  const xpToNext = next ? next.xpNeeded - current.xpNeeded : 1;
  const pct = next ? Math.min(100, Math.round((xpInLevel / xpToNext) * 100)) : 100;
  return { current, next, xpInLevel, xpToNext, pct };
};

const LevelUpHubPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [areas, setAreas] = useState(AREA_CONFIG.map(a => ({ ...a, xp: 0 })));
  const [metas, setMetas] = useState([]);
  const [xpAnimated, setXpAnimated] = useState(0);

  useEffect(() => {
    const fetchAll = async () => {
      const [profileRes, metasRes] = await Promise.allSettled([
        api.get('/pdi/profile'),
        api.get('/pdi/metas'),
      ]);

      if (profileRes.status === 'fulfilled') {
        const profileData = profileRes.value?.data?.data;
        if (profileData) {
          setProfile(profileData);
          const apiAreas = profileData.areas || [];
          setAreas(
            AREA_CONFIG.map(cfg => {
              const found = apiAreas.find(a => a.area_id === cfg.id);
              return { ...cfg, xp: found?.xp || 0 };
            })
          );
        }
      } else {
        console.error('[Hub] Erro ao carregar perfil PDI:', profileRes.reason);
      }

      if (metasRes.status === 'fulfilled') {
        setMetas(metasRes.value?.data?.records || []);
      } else {
        console.error('[Hub] Erro ao carregar metas:', metasRes.reason);
      }
    };
    fetchAll();
  }, []);

  const totalXp = profile?.totalXp ?? 0;
  const levelInfo = useMemo(() => getLevelInfo(totalXp), [totalXp]);

  useEffect(() => {
    const t = setTimeout(() => setXpAnimated(levelInfo.pct), 220);
    return () => clearTimeout(t);
  }, [levelInfo.pct]);

  const recentXpEvents = useMemo(() => profile?.recentEvents || [], [profile]);
  const metasAtivas = useMemo(() => metas.filter(m => !m.concluida).slice(0, 4), [metas]);

  const tarefasConcluidas = profile?.breakdown?.taskXp
    ? Math.floor(profile.breakdown.taskXp / 10)
    : 0;
  const habitosAtivos = 0; // incluído no XP global via /pdi/profile

  const nomeUsuario = user?.nome || user?.name || user?.email?.split('@')[0] || 'Jogador';

  const iconForTipo = (tipo) => {
    if (tipo === 'meta') return faTrophy;
    if (tipo === 'habito') return faFire;
    return faCalendarCheck;
  };

  const colorForTipo = (tipo) => {
    if (tipo === 'meta') return '#10b981';
    if (tipo === 'habito') return '#f59e0b';
    return '#6366f1';
  };

  return (
    <div className="luhub-page">
      <div className="luhub-orb luhub-orb-1" />
      <div className="luhub-orb luhub-orb-2" />
      <div className="luhub-orb luhub-orb-3" />

      {/* Hero */}
      <div className="luhub-hero">
        <div className="luhub-hero-left">
          <div className="luhub-level-badge">
            <span className="luhub-level-num">{levelInfo.current.level}</span>
          </div>
          <div className="luhub-hero-info">
            <p className="luhub-greeting">Olá, {nomeUsuario}</p>
            <h1 className="luhub-title">{levelInfo.current.title}</h1>
            <p className="luhub-xp-label">
              <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', marginRight: 5 }} />
              {totalXp.toLocaleString('pt-BR')} XP total
              {levelInfo.next && (
                <span className="luhub-next-label">
                  · faltam {(levelInfo.xpToNext - levelInfo.xpInLevel).toLocaleString('pt-BR')} XP para {levelInfo.next.title}
                </span>
              )}
            </p>
            <div className="luhub-xp-bar-wrap">
              <div className="luhub-xp-bar">
                <div className="luhub-xp-fill" style={{ '--pct': `${xpAnimated}%` }} />
              </div>
              <span className="luhub-xp-pct">{levelInfo.pct}%</span>
            </div>
          </div>
        </div>

        <div className="luhub-hero-stats">
          <div className="luhub-stat">
            <FontAwesomeIcon icon={faCalendarCheck} className="luhub-stat-icon" />
            <strong>{tarefasConcluidas}</strong>
            <small>Tarefas concluídas</small>
          </div>
          <div className="luhub-stat">
            <FontAwesomeIcon icon={faFire} className="luhub-stat-icon" style={{ color: '#f59e0b' }} />
            <strong>{Math.floor((profile?.breakdown?.habitXp || 0) / 5)}</strong>
            <small>Hábitos concluídos</small>
          </div>
          <div className="luhub-stat">
            <FontAwesomeIcon icon={faTrophy} className="luhub-stat-icon" style={{ color: '#10b981' }} />
            <strong>{metasAtivas.length}</strong>
            <small>Metas ativas</small>
          </div>
        </div>
      </div>

      {/* Life areas */}
      <section className="luhub-section">
        <div className="luhub-section-header">
          <h2 className="luhub-section-title">
            <FontAwesomeIcon icon={faStar} style={{ color: '#f59e0b', marginRight: 8 }} />
            Áreas de Vida
          </h2>
          <button className="luhub-link-btn" onClick={() => navigate('/metas')}>
            Ver Metas <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 4 }} />
          </button>
        </div>
        <div className="luhub-areas-grid">
          {areas.map(area => {
            const li = getLevelInfo(area.xp || 0);
            return (
              <div
                key={area.id}
                className="luhub-area-card"
                style={{ '--area-color': area.color, '--area-glow': area.glow }}
                onClick={() => navigate('/metas')}
              >
                <div
                  className="luhub-area-icon-wrap"
                  style={{
                    background: `${area.color}26`,
                    border: `1px solid ${area.color}44`,
                    color: area.color,
                    boxShadow: `0 0 18px ${area.glow}`,
                  }}
                >
                  <FontAwesomeIcon icon={area.icon} />
                </div>
                <div className="luhub-area-info">
                  <span className="luhub-area-label">{area.label}</span>
                  <span className="luhub-area-level">Nível {li.current.level}</span>
                  <div className="luhub-area-bar">
                    <div
                      className="luhub-area-fill"
                      style={{ width: `${li.pct}%`, background: area.color, boxShadow: `0 0 8px ${area.glow}` }}
                    />
                  </div>
                  <span className="luhub-area-xp">{(area.xp || 0).toLocaleString('pt-BR')} XP</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Goals + Activity */}
      <div className="luhub-bottom-grid">
        <section className="luhub-panel">
          <div className="luhub-panel-header">
            <h3>
              <FontAwesomeIcon icon={faTrophy} style={{ color: '#f59e0b', marginRight: 8 }} />
              Metas Ativas
            </h3>
            <button className="luhub-link-btn" onClick={() => navigate('/metas')}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          {metasAtivas.length === 0 ? (
            <div className="luhub-empty" onClick={() => navigate('/metas')}>
              <FontAwesomeIcon icon={faPlus} className="luhub-empty-icon" />
              <p>Nenhuma meta definida.</p>
              <span>Criar primeira meta →</span>
            </div>
          ) : (
            <div className="luhub-goals-list">
              {metasAtivas.map(meta => {
                const cfg = AREA_CONFIG.find(a => a.id === meta.area);
                return (
                  <div key={meta.id} className="luhub-goal-row" onClick={() => navigate('/metas')}>
                    <div className="luhub-goal-left">
                      <div className="luhub-goal-dot" style={{ background: cfg?.color || '#6366f1' }} />
                      <span className="luhub-goal-label">{meta.titulo}</span>
                    </div>
                    <div className="luhub-goal-right">
                      <div className="luhub-goal-bar">
                        <div className="luhub-goal-fill" style={{ width: `${meta.progresso || 0}%` }} />
                      </div>
                      <span className="luhub-goal-pct">{meta.progresso || 0}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="luhub-panel">
          <div className="luhub-panel-header">
            <h3>
              <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', marginRight: 8 }} />
              Atividade XP
            </h3>
          </div>
          {recentXpEvents.length === 0 ? (
            <div className="luhub-empty">
              <FontAwesomeIcon icon={faBolt} className="luhub-empty-icon" />
              <p>Nenhuma atividade ainda.</p>
              <span>Complete tarefas e conclua metas para ganhar XP</span>
            </div>
          ) : (
            <div className="luhub-activity-list">
              {recentXpEvents.map((ev, i) => (
                <div key={i} className="luhub-activity-row">
                  <div
                    className="luhub-activity-icon"
                    style={{ background: `${colorForTipo(ev.tipo)}22`, color: colorForTipo(ev.tipo) }}
                  >
                    <FontAwesomeIcon icon={iconForTipo(ev.tipo)} />
                  </div>
                  <span className="luhub-activity-label">{ev.descricao}</span>
                  <span className="luhub-activity-xp">+{ev.xp} XP</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Navigation shortcuts */}
      <div className="luhub-nav-cards">
        <div className="luhub-nav-card" onClick={() => navigate('/levelup/dia')}>
          <div className="luhub-nav-icon-wrap" style={{ background: 'rgba(99,102,241,0.16)', color: '#818cf8' }}>
            <FontAwesomeIcon icon={faCalendarDay} />
          </div>
          <div>
            <strong>Planejar Hoje</strong>
            <small>Defina seu foco e tarefas do dia</small>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="luhub-nav-arrow" />
        </div>
        <div className="luhub-nav-card" onClick={() => navigate('/levelup/semana')}>
          <div className="luhub-nav-icon-wrap" style={{ background: 'rgba(16,185,129,0.16)', color: '#10b981' }}>
            <FontAwesomeIcon icon={faCalendarWeek} />
          </div>
          <div>
            <strong>Visão Semanal</strong>
            <small>Cobertura de metas por dia</small>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="luhub-nav-arrow" />
        </div>
        <div className="luhub-nav-card" onClick={() => navigate('/skills')}>
          <div className="luhub-nav-icon-wrap">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div>
            <strong>Habilidades</strong>
            <small>Acompanhe sua evolução técnica</small>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="luhub-nav-arrow" />
        </div>
        <div className="luhub-nav-card" onClick={() => navigate('/metas')}>
          <div className="luhub-nav-icon-wrap" style={{ background: 'rgba(245,158,11,0.16)', color: '#fbbf24' }}>
            <FontAwesomeIcon icon={faMedal} />
          </div>
          <div>
            <strong>Metas & Objetivos</strong>
            <small>Hierarquia anual → trimestral</small>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="luhub-nav-arrow" />
        </div>
      </div>
    </div>
  );
};

export default LevelUpHubPage;
